import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { toast } from "sonner";
import {
  loadSnapshot,
  incrementShare,
} from "@/lib/familySnapshot.functions";
import {
  bandMessage,
  scoreBand,
  formatUSD,
  type GapResult,
  type Recommendation,
} from "@/lib/familySnapshot";
import { Share2, Download, Calendar } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { CareerInviteCard } from "@/components/conversion/CareerInviteCard";

export const Route = createFileRoute("/tools/family-snapshot/result/$token")({
  head: () => ({
    meta: [
      { title: "Your Family Snapshot — Currency Thoughts" },
      {
        name: "description",
        content:
          "Your personal Family Snapshot — protection score, coverage gap, and recommended next steps.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SnapshotResultPage,
});

type SnapshotRow = {
  id: string;
  email: string | null;
  household: { your_age?: number; spouse_age?: number | null; dependents?: { age: number }[] };
  income: { your_income?: number; spouse_income?: number | null };
  coverage: unknown;
  goals: string[];
  computed_gap: GapResult;
  protection_score: number;
  recommended_actions: Recommendation[];
  shared_count: number;
  completed_at: string | null;
  created_at: string;
};

function SnapshotResultPage() {
  const { token } = Route.useParams();
  const load = useServerFn(loadSnapshot);
  const share = useServerFn(incrementShare);
  const [snap, setSnap] = useState<SnapshotRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    load({ data: { token } })
      .then((res) => {
        if (cancel) return;
        if (!res.ok) setError(res.error);
        else {
          setSnap(res.snapshot as SnapshotRow);
          trackEvent("snapshot_completed");
        }
      })
      .catch((e) => {
        if (cancel) return;
        console.error(e);
        setError("Unable to load your snapshot.");
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [token, load]);

  async function handleShare() {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
      const res = await share({ data: { token } });
      if (res.ok && snap) setSnap({ ...snap, shared_count: res.shared_count });
    } catch {
      toast.error("Couldn't copy link");
    }
  }

  if (loading) {
    return (
      <ShellWrapper>
        <div className="mx-auto max-w-3xl px-6 py-24 text-center font-sans text-charcoal-500">
          Loading your snapshot…
        </div>
      </ShellWrapper>
    );
  }

  if (error || !snap) {
    return (
      <ShellWrapper>
        <div className="mx-auto max-w-2xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold text-navy-800">
            This snapshot link isn't valid
          </h1>
          <p className="mt-3 font-sans text-charcoal-500">
            {error ?? "The link may have expired."}
          </p>
          <div className="mt-8">
            <PrimaryButton asChild>
              <Link to="/tools/family-snapshot">Start a new snapshot</Link>
            </PrimaryButton>
          </div>
        </div>
      </ShellWrapper>
    );
  }

  const date = new Date(snap.completed_at ?? snap.created_at);
  const dateLabel = date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const band = scoreBand(snap.protection_score);
  const topRecs = (snap.recommended_actions ?? []).slice(0, 5);

  return (
    <ShellWrapper>
      {/* Header */}
      <section className="bg-navy-900 text-cream-50 py-14">
        <div className="mx-auto max-w-4xl px-6">
          <Eyebrow color="bronze">Your Snapshot · {dateLabel}</Eyebrow>
          <h1
            className="mt-5 font-display font-bold text-cream-50"
            style={{ fontSize: "44px", lineHeight: 1.15, letterSpacing: "-0.01em" }}
          >
            Your <em className="text-bronze-400 italic">Family Snapshot.</em>
          </h1>
        </div>
      </section>

      {/* Score hero */}
      <section className="bg-cream-50 py-12">
        <div className="mx-auto max-w-4xl px-6">
          <div
            className="bg-white border-t-[3px] border-bronze-600 shadow-soft px-8 py-10 text-center"
            style={{ borderRadius: "4px" }}
          >
            <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-charcoal-500">
              Family Protection Score
            </p>
            <div
              className="mt-3 font-display font-bold text-bronze-600"
              style={{ fontSize: "64px", lineHeight: 1 }}
            >
              {snap.protection_score}
              <span className="font-sans text-[20px] font-normal text-charcoal-500">
                /100
              </span>
            </div>
            <p className="mx-auto mt-4 max-w-xl font-sans text-[15px] leading-relaxed text-charcoal-900">
              {bandMessage(band)}
            </p>
          </div>
        </div>
      </section>

      {/* Where you stand */}
      <section className="bg-cream-50 pb-12">
        <div className="mx-auto max-w-4xl px-6">
          <SectionHeader eyebrow="Where You Stand" title="Coverage vs. recommended" />
          <div
            className="mt-6 bg-white shadow-soft px-8 py-8"
            style={{ borderRadius: "4px" }}
          >
            <CoverageBar
              label="You"
              current={snap.computed_gap.you.current}
              recommended={snap.computed_gap.you.recommended}
            />
            {snap.computed_gap.spouse && (
              <div className="mt-8">
                <CoverageBar
                  label="Spouse / partner"
                  current={snap.computed_gap.spouse.current}
                  recommended={snap.computed_gap.spouse.recommended}
                />
              </div>
            )}
            <p className="mt-6 border-t border-bronze-600/20 pt-4 font-sans text-[12px] italic text-charcoal-500">
              Recommended values use the DIME framework (Debt, Income×10, Mortgage,
              Education) as a planning guide — not a prescription.
            </p>
          </div>
        </div>
      </section>

      {/* What we noticed */}
      <section className="bg-cream-50 pb-12">
        <div className="mx-auto max-w-4xl px-6">
          <SectionHeader
            eyebrow="What We Noticed"
            title="Your highest-leverage next steps"
          />
          <ol className="mt-6 space-y-4">
            {topRecs.map((rec, idx) => (
              <li
                key={rec.id}
                className="bg-white shadow-soft border-l-[3px] border-bronze-600 px-6 py-5"
                style={{ borderRadius: "4px" }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-bronze-600 bg-navy-900 font-display text-[16px] font-bold text-cream-50">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <PriorityPill priority={rec.priority} />
                      <h3 className="font-display text-[20px] font-semibold text-navy-800">
                        {rec.title}
                      </h3>
                    </div>
                    <p className="mt-2 font-sans text-[14px] leading-relaxed text-charcoal-900">
                      {rec.description}
                    </p>
                    <div className="mt-3">
                      <Link
                        to={rec.cta_href}
                        className="font-sans text-[12px] font-semibold uppercase tracking-[0.12em] text-bronze-600 hover:text-bronze-400"
                      >
                        {rec.cta_label} →
                      </Link>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* What to do next */}
      <section className="bg-cream-50 pb-16">
        <div className="mx-auto max-w-4xl px-6">
          <SectionHeader eyebrow="What To Do Next" title="Three paths forward" />
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <ActionCard
              icon={<Calendar className="h-5 w-5" />}
              title="Schedule a 30-min review"
              description="A Strategist walks through your snapshot with you."
              cta={
                <PrimaryButton asChild>
                  <Link to="/book">Book a time →</Link>
                </PrimaryButton>
              }
            />
            <ActionCard
              icon={<Download className="h-5 w-5" />}
              title="Download the action checklist"
              description="A printable one-page version of your snapshot."
              cta={
                <button
                  type="button"
                  onClick={() => toast.info("PDF export coming soon.")}
                  className="font-sans text-[12px] font-semibold uppercase tracking-[0.12em] text-bronze-600 hover:text-bronze-400"
                >
                  Download PDF →
                </button>
              }
            />
            <ActionCard
              icon={<Share2 className="h-5 w-5" />}
              title="Share with your spouse"
              description={`Send this link so you can review it together.${
                snap.shared_count ? ` (Shared ${snap.shared_count}×)` : ""
              }`}
              cta={
                <button
                  type="button"
                  onClick={handleShare}
                  className="font-sans text-[12px] font-semibold uppercase tracking-[0.12em] text-bronze-600 hover:text-bronze-400"
                >
                  Copy link →
                </button>
              }
            />
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-navy-800 text-cream-50 py-12">
        <div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-5 px-6 sm:flex-row sm:text-left">
          <div>
            <h3 className="font-display text-[26px] font-semibold text-cream-50">
              Want a Strategist to walk through this with you?
            </h3>
            <p className="mt-1 font-sans text-[14px] text-cream-50/80">
              30 minutes. No pitch. Educational.
            </p>
          </div>
          <PrimaryButton asChild size="lg">
            <Link to="/book">Book a review</Link>
          </PrimaryButton>
        </div>
      </section>

      {/* Compliance footer */}
      <section className="bg-cream-50 py-8">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-center font-sans text-[12px] italic leading-relaxed text-charcoal-500">
            Projections shown are hypothetical and for educational purposes only.
            They do not represent guaranteed returns or actual policy performance.
            Currency Thoughts LLC is an independent insurance agency. Products and
            availability vary by state.
          </p>
        </div>
      </section>
      <CareerInviteCard source="snapshot" variant="footer" />
    </ShellWrapper>
  );
}

/* ─────────────── sub-components ─────────────── */

function ShellWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">{children}</main>
      <Footer />
    </div>
  );
}

function SectionHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <Eyebrow color="bronze">{eyebrow}</Eyebrow>
      <h2 className="mt-3 font-display text-[32px] font-semibold text-navy-800">
        {title}
      </h2>
    </div>
  );
}

function CoverageBar({
  label,
  current,
  recommended,
}: {
  label: string;
  current: number;
  recommended: number;
}) {
  const max = Math.max(recommended, current, 1);
  const currentPct = (current / max) * 100;
  const gap = Math.max(0, recommended - current);
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <p className="font-sans text-[13px] font-semibold uppercase tracking-[0.14em] text-navy-800">
          {label}
        </p>
        <p className="font-sans text-[12px] text-charcoal-500">
          Gap: <span className="font-semibold text-navy-800">{formatUSD(gap)}</span>
        </p>
      </div>
      <div
        className="relative h-10 w-full border border-bronze-400 bg-cream-50"
        style={{ borderRadius: "2px" }}
      >
        {/* recommended outline = container; current = filled navy bar */}
        <div
          className="h-full bg-navy-800 transition-[width]"
          style={{ width: `${currentPct}%`, borderRadius: "2px" }}
        />
      </div>
      <div className="mt-2 flex justify-between font-sans text-[12px] text-charcoal-500">
        <span>
          Current: <span className="font-semibold text-navy-800">{formatUSD(current)}</span>
        </span>
        <span>
          Recommended:{" "}
          <span className="font-semibold text-bronze-600">{formatUSD(recommended)}</span>
        </span>
      </div>
    </div>
  );
}

function PriorityPill({ priority }: { priority: Recommendation["priority"] }) {
  const map = {
    high: { label: "High priority", cls: "bg-danger/10 text-danger border-danger/30" },
    medium: {
      label: "Worth reviewing",
      cls: "bg-warning/10 text-warning border-warning/30",
    },
    low: { label: "Next step", cls: "bg-navy-800/5 text-navy-800 border-navy-800/20" },
  } as const;
  const m = map[priority];
  return (
    <span
      className={`inline-flex items-center border px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.14em] ${m.cls}`}
      style={{ borderRadius: "2px" }}
    >
      {m.label}
    </span>
  );
}

function ActionCard({
  icon,
  title,
  description,
  cta,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: React.ReactNode;
}) {
  return (
    <div
      className="flex h-full flex-col bg-white shadow-soft px-6 py-6"
      style={{ borderRadius: "4px" }}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-bronze-600/10 text-bronze-600">
        {icon}
      </div>
      <h3 className="mt-4 font-display text-[18px] font-semibold text-navy-800">
        {title}
      </h3>
      <p className="mt-1 flex-1 font-sans text-[13px] leading-relaxed text-charcoal-500">
        {description}
      </p>
      <div className="mt-4">{cta}</div>
    </div>
  );
}